import { get } from 'lodash';
import { mapGetters } from 'vuex';
import Constants, { ItemTypeConsts, StoreModelMap, StoreModuleMap } from 'client/util/Constants';
import Util from 'client/util/Util';

const { unknown } = Constants;

const systemModel = StoreModelMap[ItemTypeConsts.system];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

export default {
  name: 'SystemInsightsComponentMixin',
  computed: {
    ...mapGetters(systemModel, {
      isSystemInsightsSupported: 'isSystemInsightsSupported',
      isWindows: 'isWindows',
      showSystemInsights: 'showSystemInsights',
    }),

    ...mapGetters(settingsModule, ['hasSystemInsights']),

    deviceSystemInsightsEnabled() {
      return this.item.systemInsights.state === 'enabled';
    },
  },

  methods: {
    formatMemoryValue(baseMemory) {
      const value = parseInt(baseMemory, 10);

      if (value === -1 || Number.isNaN(value)) {
        return { number: Constants.dataNotAvailable, unit: '', percentage: '' };
      }

      return Util.getTextToDisplayBytes(value, this.isWindows);
    },

    formatAvailableStorageValue(storage) {
      let availableStorage = { number: Constants.dataNotAvailable, unit: '', percentage: '' };

      if (storage && storage.length) {
        // Find largest drive
        const sizeKey = this.isWindows ? 'size' : 'blocks';
        let maxDriveIndex = 0;
        let maxSize = 0;
        storage.forEach((drive, index) => {
          let size = parseInt(drive[sizeKey], 10);
          if (Number.isNaN(size)) return;
          if (!this.isWindows) {
            // If Mac or Linux, converts blocks to bytes
            size *= parseInt(drive.blocks_size, 10);
          }
          if (size > maxSize) {
            maxSize = size;
            maxDriveIndex = index;
          }
        });
        const largestDrive = storage[maxDriveIndex];

        // Get amount of free space on largest drive
        const freeSpaceKey = this.isWindows ? 'free_space' : 'blocks_available';
        let freeSpace = parseInt(largestDrive[freeSpaceKey], 10);

        if (Number.isNaN(freeSpace)) {
          return availableStorage;
        }

        if (!this.isWindows) {
          // If Mac or Linux, converts blocks to bytes
          freeSpace *= parseInt(largestDrive.blocks_size, 10);
        }

        // Set display values of drive size and percentage of free space on drive
        const storageValue = Util.getTextToDisplayBytes(freeSpace, this.isWindows);
        let percentage = '';
        if (maxSize > 0) {
          percentage = `${Math.ceil((100 * freeSpace) / maxSize)}%`;
        }

        availableStorage = { ...storageValue, percentage };
      }

      // eslint-disable-next-line consistent-return
      return availableStorage;
    },

    formatOsVersionValue(systemInfo, osVersionDetail) {
      let osVersion;

      if (this.isWindows) {
        // The system insights os_version response does not contain the revision number so
        // this is grabbing it from the osVersionDetail on the system object
        const patch = get(osVersionDetail, 'patch');
        const revision = get(osVersionDetail, 'revision');
        const majorMinor = systemInfo.major && systemInfo.minor ? `${systemInfo.major}.${systemInfo.minor}` : systemInfo.major;
        const windowsBuild = patch && revision ? `${patch}.${revision}` : revision;
        if (majorMinor) {
          const versionString = `${majorMinor} ${windowsBuild ? `(${windowsBuild})` : ''}`;
          const versionStringFormatted = versionString.replace(/\s+/g, ' ').trim();
          osVersion = versionStringFormatted;
        } else {
          osVersion = this.unknown || unknown;
        }
      } else if (systemInfo.version) {
        osVersion = `${systemInfo.version} ${systemInfo.build ? `(${systemInfo.build})` : ''}`.trim();
      } else {
        osVersion = this.unknown || unknown;
      }

      return osVersion;
    },

    handleSystemInsightsChange() {
      if (!this.deviceSystemInsightsEnabled) {
        this.showDeviceEnableSystemInsightsModal = true;
        const value = { state: 'enabled' };
        const key = 'systemInsights';
        this.$emit('handleValueChange', value, key);
      }
    },

    handleUpdateSystemInsightsModalVisibility(visible) {
      this.showDeviceEnableSystemInsightsModal = visible;
    },
  },

  props: {
    item: {
      type: Object,
      required: true,
    },

    storageInfo: {
      type: Array,
      default: () => [],
    },

    systemInfo: {
      type: Object,
      default: () => ({}),
    },
  },
};
